// Transport layer constants for REQ-6.8.
// Centralised here so zoom thresholds, layer IDs, and colors are never hardcoded in components.

import { TRANSPORT_FEATURE_MODES } from '@ooh/contracts';

// ─── MapLibre source / layer IDs ─────────────────────────────────────────────

export const TRANSPORT_SOURCE_ID = 'transport-source';
export const TRANSPORT_PUBLIC_OUTLINE_LAYER_ID = 'transport-public-outline';
export const TRANSPORT_PUBLIC_LAYER_ID = 'transport-public';
export const TRANSPORT_ROADS_OUTLINE_LAYER_ID = 'transport-roads-outline';
export const TRANSPORT_ROADS_LAYER_ID = 'transport-roads';

// ─── Zoom thresholds ──────────────────────────────────────────────────────────

/** Minimum zoom for public transport lines to be visible (city scale). */
export const CITY_ZOOM_THRESHOLD = 10.5;

/** Minimum zoom for major road lines to be visible (regional scale). */
export const ROAD_ZOOM_THRESHOLD = 7.5;

// ─── Layer paint constants ────────────────────────────────────────────────────

/** Road outline color (white-ish) */
export const ROAD_OUTLINE_COLOR = 'rgba(255,255,255,0.92)';

/** Road line color (amber) */
export const ROAD_LINE_COLOR = 'rgba(245,158,11,0.82)';

/** Public transport outline color (near-white) */
export const PUBLIC_OUTLINE_COLOR = 'rgba(255,255,255,0.95)';

/**
 * Mode-specific public transport line colors.
 * Keys match TransportFeatureMode values from @ooh/contracts.
 */
export const MODE_COLORS = {
  autobusy: '#0ea5e9', // sky-500
  tramwaje: '#d946ef', // fuchsia-500
  'kolej-metro': '#6366f1', // indigo-500
  metro: '#10b981', // emerald-500
  road: ROAD_LINE_COLOR,
} as const satisfies Record<(typeof TRANSPORT_FEATURE_MODES)[number], string>;

/** Fallback color for unknown modes */
export const MODE_COLOR_FALLBACK = '#0ea5e9';

/**
 * Mode-specific dash array values for public transport lines.
 * Keys match TransportFeatureMode values from @ooh/contracts.
 * Each value is [dash-length, gap-length] in line-width units.
 * Used to derive the MapLibre 'line-dasharray' match expression.
 */
// Road lines use a solid stroke — no dash-array entry for 'road'.
// Partial<> is intentional: locks keys to canonical modes while allowing the road mode to be absent.
export const MODE_DASH_ARRAYS = {
  autobusy: [1, 1.4],
  tramwaje: [0.9, 0.6],
  'kolej-metro': [2, 0.8],
  metro: [1, 0],
} as const satisfies Partial<
  Record<(typeof TRANSPORT_FEATURE_MODES)[number], readonly [number, number]>
>;

/** Fallback dash array for unknown public transport modes */
export const MODE_DASH_ARRAY_FALLBACK: [number, number] = [1, 1.4];

// ─── Mode type i18n keys ──────────────────────────────────────────────────────
// Maps transport mode values to their i18n key under 'map' namespace.
// Components must call t(key) to get the translated label — do not use raw strings.

export const MODE_TYPE_I18N_KEY = {
  road: 'transportLayers.modeLabels.road',
  autobusy: 'transportLayers.modeLabels.autobusy',
  tramwaje: 'transportLayers.modeLabels.tramwaje',
  'kolej-metro': 'transportLayers.modeLabels.kolej-metro',
  metro: 'transportLayers.modeLabels.metro',
} as const satisfies Record<(typeof TRANSPORT_FEATURE_MODES)[number], string>;

/** Fallback i18n key for unknown transport modes. */
export const MODE_TYPE_UNKNOWN_I18N_KEY = 'transportLayers.modeLabels.unknown';

/** i18n key values for public transport modes (used to detect scope from popup state). */
export const PUBLIC_TYPE_I18N_KEYS = new Set([
  'transportLayers.modeLabels.autobusy',
  'transportLayers.modeLabels.tramwaje',
  'transportLayers.modeLabels.kolej-metro',
  'transportLayers.modeLabels.metro',
]);

// ─── Legend colors (line swatches) ────────────────────────────────────────────

export const LEGEND_PUBLIC_COLOR = '#0ea5e9'; // use bus color as representative
export const LEGEND_ROADS_COLOR = '#f59e0b'; // amber

// ─── The id of the MediaUnit cluster layer (used as beforeId when adding transport layers) ────

export const PIN_CLUSTER_LAYER_ID = 'clusters';
